import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "../lib/utils";
import { OVERLAY_COLLISION_PADDING, OVERLAY_FADE, OVERLAY_OFFSET, OVERLAY_SURFACE } from "../lib/overlay";

/**
 * v2 Popover (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap. Moves from
 * Venus `components/ui/popover.tsx` into frontend-common under the same names.
 *
 * Interactive content (filters, pickers, forms) that opens on CLICK. A plain
 * text label is a Tooltip. There is no open-on-hover mode: the old HoverCard
 * sites hovered elements that keyboard and touch can't reach (break #80).
 *
 * v1 PopoverContent was overridden at all 18 sites (`p-0` ×9, six widths), so
 * padding and width are props (break #79):
 * - `padding`: `md` 16 (default) or `none` for lists and sectioned content.
 * - `width`: `sm` 240, `md` 320 (default), `lg` 384, `trigger` (at least the
 *   trigger's width), `auto` (the content's).
 * The height is capped at the space Radix measures next to the trigger; the
 * content scrolls inside it.
 */

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverClose = PopoverPrimitive.Close;

export type PopoverPadding = "none" | "md";
export type PopoverWidth = "sm" | "md" | "lg" | "trigger" | "auto";

const PADDING: Record<PopoverPadding, string> = {
  none: "p-0",
  md: "p-4",
};

const WIDTH: Record<PopoverWidth, string> = {
  sm: "w-60",
  md: "w-80",
  lg: "w-96",
  trigger: "",
  auto: "w-auto",
};

export interface PopoverContentProps extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  padding?: PopoverPadding;
  width?: PopoverWidth;
}

const PopoverContent = React.forwardRef<React.ElementRef<typeof PopoverPrimitive.Content>, PopoverContentProps>(
  function PopoverContent(
    {
      padding = "md",
      width = "md",
      align = "center",
      sideOffset = OVERLAY_OFFSET,
      collisionPadding = OVERLAY_COLLISION_PADDING,
      className,
      style,
      ...props
    },
    ref,
  ) {
    return (
      // Portalled so card and table `overflow` can't clip it; tokens and `.dark` sit on <html>.
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          className={cn(OVERLAY_SURFACE, OVERLAY_FADE, "overflow-y-auto", PADDING[padding], WIDTH[width], className)}
          // Radix's measured sizes as inline styles: an `(--var)` class would ship to the apps as one-off CSS.
          style={{
            maxHeight: "var(--radix-popover-content-available-height)",
            ...(width === "trigger" && { minWidth: "var(--radix-popover-trigger-width)" }),
            ...style,
          }}
          {...props}
        />
      </PopoverPrimitive.Portal>
    );
  },
);

export { Popover, PopoverTrigger, PopoverAnchor, PopoverClose, PopoverContent };
